import { strict as assert } from 'assert';
import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec3,
    mat3,
    mat4,
    quat,
    vec3,
} from 'gl-matrix';
import { BoundingBox } from './BoundingBox';

/**
 * Represents a Grid like object dimensions in 3D.
 */
export class Grid {
    private readonly size: ReadonlyVec3;
    private readonly res: ReadonlyVec3;
    private readonly transformation: ReadonlyMat4;
    private readonly _voxelToWorld: ReadonlyMat4;
    private readonly _worldToVoxel: ReadonlyMat4;

    /**
     * Construct a new Grid object
     *
     * @param sizex - X size in voxels, must be >= 0 and integer
     * @param sizey - Y size in voxels, must be >= 0 and integer
     * @param sizez - Z size in voxels, must be >= 0 and integer
     * @param resx - X resolution in mm, must be >= 0
     * @param resy - Y resolution in mm, must be >= 0
     * @param resz - Z resolution in mm, must be >= 0
     * @param transformation - transformation matrix for the object, may be null
     */
    constructor(
        sizex: number,
        sizey: number,
        sizez: number,
        resx: number,
        resy: number,
        resz: number,
        transformation: ReadonlyMat4 | null
    ) {
        assert(Number.isInteger(sizex), 'Grid X size must be integer');
        assert(Number.isInteger(sizey), 'Grid Y size must be integer');
        assert(Number.isInteger(sizez), 'Grid Z size must be integer');
        assert(sizex >= 0, 'Grid X size must be positive');
        assert(sizey >= 0, 'Grid Y size must be positive');
        assert(sizez >= 0, 'Grid Z size must be positive');
        assert(resx >= 0, 'Grid X resolution must be positive');
        assert(resy >= 0, 'Grid Y resolution must be positive');
        assert(resz >= 0, 'Grid Z resolution must be positive');
        this.size = vec3.fromValues(sizex, sizey, sizez);
        this.res = vec3.fromValues(resx, resy, resz);
        this.transformation = transformation
            ? mat4.clone(transformation)
            : mat4.create();

        this._voxelToWorld = mat4.multiply(
            mat4.create(),
            this.transformation,
            mat4.fromScaling(mat4.create(), this.res)
        );
        this._worldToVoxel = mat4.invert(mat4.create(), this._voxelToWorld);
    }

    /**
     * Helper function to create a Grid object from DICOM data
     * @param size grid size
     * @param res grid resolution
     * @param translation grid translation, the bottom slize position
     * @param orientation grid orientation matrix, the DICOM Image Orientation (Patient) attribute
     * @returns
     */
    public static fromDicom(
        size: ReadonlyVec3,
        res: ReadonlyVec3,
        translation: ReadonlyVec3,
        orientation: ReadonlyMat3
    ): Grid {
        const transformation = mat4.fromRotationTranslation(
            mat4.create(),
            quat.fromMat3(quat.create(), orientation),
            translation
        );

        return new Grid(
            size[0],
            size[1],
            size[2],
            res[0],
            res[1],
            res[2],
            transformation
        );
    }

    public static clone(grid: Grid): Grid {
        return new Grid(
            grid.sizeX,
            grid.sizeY,
            grid.sizeZ,
            grid.resX,
            grid.resY,
            grid.resZ,
            grid.transformation
        );
    }

    /**
     * Size of the Grid (x,y,z)
     * @returns Size as a {@link ReadonlyVec3}
     */
    getSize(): ReadonlyVec3 {
        return this.size;
    }

    get sizeX(): number {
        return this.size[0];
    }
    get sizeY(): number {
        return this.size[1];
    }
    get sizeZ(): number {
        return this.size[2];
    }

    /**
     * Resolution of the Grid (x,y,z)
     * @returns Resolution as a {@link ReadonlyVec3}
     */
    getRes(): ReadonlyVec3 {
        return this.res;
    }

    get resX(): number {
        return this.res[0];
    }
    get resY(): number {
        return this.res[1];
    }
    get resZ(): number {
        return this.res[2];
    }

    /**
     * Size of the Grid (x,y,z) in mm
     * @returns Size as a {@link ReadonlyVec3}
     */
    getSizeMM(): ReadonlyVec3 {
        return vec3.mul(vec3.create(), this.size, this.res);
    }

    /**
     * Get the X size in mm
     */
    get sizeXMM(): number {
        return this.size[0] * this.res[0];
    }

    /**
     * Get the Y size in mm
     */
    get sizeYMM(): number {
        return this.size[1] * this.res[1];
    }

    /**
     * Get the Z size in mm
     */
    get sizeZMM(): number {
        return this.size[2] * this.res[2];
    }

    get center(): vec3 {
        return vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(
                this.size[0] / 2,
                this.size[1] / 2,
                this.size[2] / 2
            ),
            this.voxelToWorld
        );
    }

    /**
     * Get the transformation matrix of this Grid
     * @returns transformation matrix as a {@link ReadonlyMat4} or null if not defined
     */
    getTransformation(): ReadonlyMat4 {
        return this.transformation;
    }

    /**
     * Patient coordinates to Voxel coordinates transformation matrix
     */
    get worldToVoxel(): ReadonlyMat4 {
        return this._worldToVoxel;
    }

    /**
     * Voxel coordinates to Patient coordinates transformation matrix
     */
    get voxelToWorld(): ReadonlyMat4 {
        return this._voxelToWorld;
    }

    /**
     * Transform a position from PatientSpace to VoxelSpace
     * @param position
     * @returns
     */
    toVoxelSpace(position: ReadonlyVec3): vec3 {
        const voxelPos = vec3.transformMat4(
            vec3.create(),
            position,
            this.worldToVoxel
        );
        return voxelPos;
    }

    /**
     * Transform a position from VoxelSpace to PatientSpace
     * @param position
     * @returns
     */
    toWorldSpace(position: ReadonlyVec3): vec3 {
        const pos = vec3.transformMat4(
            vec3.create(),
            position,
            this.voxelToWorld
        );
        return pos;
    }

    /**
     * Get the normal position of the grid at a given Z index. Normal position is the DICOM coordinates offset of the slice in z direction.\
     * For usual CT scans where the patient is aligned with the imaging device, the normal position is the same as the DICOM Image Position attribute.\
     * For an oblique scan the normal position is the distance from the first slice in the slice direction (z resolution x the slice index).
     */
    public getNormalPosition(zIndex: number): number {
        return vec3.dot(
            this.toWorldSpace(vec3.fromValues(0, 0, zIndex)),
            this.getUnitZ()
        );
    }

    /**
     *
     * @returns PatientSpace coordinates of the center of the first voxel of the bottom most slice.
     */
    getPosition(): vec3 {
        return mat4.getTranslation(vec3.create(), this.transformation);
    }

    /**
     * Get the orientation matrix of the Grid \
     */
    getOrientation(): mat3 {
        return mat3.fromMat4(mat3.create(), this.transformation);
    }

    /**
     * Get the inverse of the orientation matrix of the Grid
     */
    getOrientationInverse(): mat3 {
        return mat3.transpose(mat3.create(), this.getOrientation());
    }

    /**
     * Rotate a vector in PatientSpace to the Grid orientation
     */
    public rotate(v: ReadonlyVec3): vec3 {
        return vec3.transformMat3(vec3.create(), v, this.getOrientation());
    }

    /**
     * Rotate a vector in Grid orientation to PatientSpace
     */
    public rotateInverse(v: ReadonlyVec3): vec3 {
        return vec3.transformMat3(
            vec3.create(),
            v,
            this.getOrientationInverse()
        );
    }

    /** Unit vector of X direction */
    getUnitX(): vec3 {
        const rot = this.getOrientation();
        return vec3.fromValues(rot[0], rot[1], rot[2]);
    }

    /** Unit vector of Y direction */
    getUnitY(): vec3 {
        const rot = this.getOrientation();
        return vec3.fromValues(rot[3], rot[4], rot[5]);
    }

    /** Unit vector of Z direction */
    getUnitZ(): vec3 {
        const rot = this.getOrientation();
        return vec3.fromValues(rot[6], rot[7], rot[8]);
    }

    isAxisAligned(): boolean {
        const v = this.getOrientation();
        // Check if the orientation matrix is aligned with patient axes by examining the first two vectors
        for (let i = 0; i < 6; i++) {
            if (v[i] !== 0 && Math.abs(v[i]) !== 1) {
                return false;
            }
        }
        return true;
    }

    toBoundingBox(): BoundingBox {
        return new BoundingBox(
            this.getPosition(),
            vec3.add(vec3.create(), this.getPosition(), this.getSizeMM())
        );
    }
}
